import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Track } from '../../api.js';

export interface TrackDetailHandle {
  track: Track | null;
  busy: boolean;
  error: string | null;
  act: (fn: () => Promise<unknown>) => Promise<void>;
}

export function useTrackDetail(
  id: string | null,
  tracks: Track[],
  refreshList: () => void
): TrackDetailHandle {
  const [track, setTrack] = useState<Track | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrack = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.getTrack(id);
      setTrack(r.track);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [id]);

  useEffect(() => {
    setError(null);
    if (!id) {
      setTrack(null);
      return;
    }
    const fromList = tracks.find((t) => t.id === id);
    if (fromList) {
      setTrack(fromList);
    } else {
      fetchTrack();
    }
  }, [id, tracks, fetchTrack]);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        await fetchTrack();
        refreshList();
      } catch (e) {
        if (e instanceof ApiError) setError(e.message);
        else setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [fetchTrack, refreshList]
  );

  return { track, busy, error, act };
}
